///
/// EarthBrowser
///
/// Created by Matt Giger on 1/7/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type matrix } from "./matrix"; // Add this import
import { type ray } from "./ray";
import { dot, normal, vec3 } from "./vector";

/**
 * Represents a sphere in 3D space.
 */
export class sphere {
	/**
	 * Creates a new sphere instance.
	 * @param radius - The radius of the sphere.
	 * @param center - The center point of the sphere (default: vec3.zero).
	 */
	constructor(readonly radius: number, readonly center: vec3 = vec3.zero) {}

	/**
	 * Calculates the intersection of a ray with the sphere.
	 * @param r - The ray to intersect with the sphere.
	 * @returns The distance along the ray to the intersection point, or -1 if no intersection.
	 */
	public intersect(r: ray): number {
		// Calculate the vector from the ray origin to the sphere center
		const rorig = r.origin.subtract(this.center)

		// Calculate coefficients for the quadratic equation
		const b = 2.0 * dot(rorig, r.direction)
		const c = dot(rorig, rorig) - this.radius * this.radius

		// Calculate the discriminant
		const d = b * b - 4 * c

		if(d >= 0) {
			// If discriminant is non-negative, there is an intersection
			const sd = Math.sqrt(d)
			const t = (-b - sd) / 2.0

			if(t < 0) {
				// If t is negative, return the other intersection point
				return (-b + sd) / 2.0
			}
			return t
		}

		// No intersection
		return -1
	}

	/**
	 * Checks if this sphere intersects with another sphere.
	 * @param s - The other sphere to check for intersection.
	 * @returns True if the spheres intersect, false otherwise.
	 */
	public intersectSphere(s: sphere): boolean {
		return this.radius + s.radius >= this.center.subtract(s.center).length
	}

	public closestPoint(r: ray): vec3 {
		const intersection = this.intersect(r)
		if(intersection > 0) {
			return r.pointAt(intersection)
		} else {
			// Find the closest point on the ray to the sphere's center
			const toCenter = this.center.subtract(r.origin)
			const t = Math.max(0, dot(toCenter, r.direction))
			const closestPointOnRay = r.pointAt(t)

			// Get direction from sphere center to that point and normalize
			const direction = normal(closestPointOnRay.subtract(this.center))

			// Return the point on the sphere's surface in that direction
			return this.center.add(direction.multiply(this.radius))
		}
	}

	/**
	 * Transforms the sphere by a matrix.
	 * @param m - The transformation matrix.
	 * @returns A new sphere instance with the transformed center and scaled radius.
	 */
	public transform(xform: matrix): sphere {
		const center = xform.multVec3(this.center, 1)
		const radius = this.radius * xform.scalingFactor()
		return new sphere(radius, center)
	}
}
